import { useEffect } from 'react';
import { AlertTriangle, ListChecks, X } from 'lucide-react';
import type { DeviceFault, DeviceTask } from '@simpleacs/shared';
import { formatRelative, formatTimestamp } from '../../lib/format';
import { Button, Card, Pill } from '../../components/ui';

/**
 * What a queued task or a recorded fault actually says.
 *
 * The tables can fit a name and a truncated message, and neither answers the
 * question somebody opens this tab with: a column of `setParameterValues` says
 * nothing about which parameters, and `9003 Invalid arguments` says nothing
 * about which argument. Both are here in full, in the CPE's own words.
 */
export function TaskDetailDialog({
  task,
  fault,
  onClose,
  onCancel,
}: {
  task?: DeviceTask | null;
  fault?: DeviceFault | null;
  onClose: () => void;
  /**
   * Offered for a task, since a stuck one is usually read in order to cancel
   * it. The parent closes this dialog and runs the delete in the tasks tray,
   * so there is no spinner here.
   */
  onCancel?: () => void;
}) {
  // Escape closes it, the way every other dialog on this page does.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);

  if (!task && !fault) return null;
  const when = task?.timestamp ?? fault?.timestamp ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={task ? `Task ${task.name}` : `Fault ${fault?.code}`}
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl">
        <Card
          title={
            <span className="flex items-center gap-2">
              {task ? (
                <ListChecks className="size-4 text-muted" />
              ) : (
                <AlertTriangle className="size-4 text-danger" />
              )}
              <span className="font-mono">{task ? task.name : fault!.code}</span>
              {fault && <Pill tone="warn">{fault.channel}</Pill>}
              {task?.fault && <Pill tone="danger">faulted</Pill>}
            </span>
          }
          action={
            <Button size="sm" variant="ghost" icon={<X className="size-4" />} onClick={onClose} />
          }
        >
          <dl className="divide-y divide-line">
            <Row label="When">
              {when ? (
                <>
                  {formatTimestamp(when)}{' '}
                  <span className="text-faint">· {formatRelative(when)}</span>
                </>
              ) : (
                <span className="text-faint">not recorded</span>
              )}
            </Row>
            <Row label="Retries">
              <span className="tabular-nums">{task ? (task.retries ?? 0) : fault!.retries}</span>
            </Row>
            {fault && <Row label="Message">{fault.message}</Row>}
            <Row label="Id">
              <span className="font-mono text-xs text-muted">{task?.id ?? fault!.id}</span>
            </Row>
          </dl>

          {/* Which parameters, which is the whole reason to open a task. */}
          {task?.parameterValues && task.parameterValues.length > 0 && (
            <Block title={`Writing ${task.parameterValues.length} parameter${task.parameterValues.length === 1 ? '' : 's'}`}>
              <table className="w-full border-collapse text-xs">
                <tbody>
                  {task.parameterValues.map(([path, value, type], i) => (
                    <tr key={`${path}-${i}`} className="align-top">
                      <td className="border-b border-line py-1.5 pr-3 font-mono text-muted">
                        {path}
                      </td>
                      <td className="border-b border-line py-1.5 font-mono text-ink">
                        {isSecret(path) ? (
                          <span className="text-faint" title="Read it on the WiFi card, which asks who is looking">
                            ••••••••
                          </span>
                        ) : value === '' ? (
                          <span className="text-faint">(empty)</span>
                        ) : (
                          value
                        )}
                        {type && <span className="ml-2 text-faint">{type}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Block>
          )}

          {task?.parameterNames && task.parameterNames.length > 0 && (
            <Block title={`Reading ${task.parameterNames.length} parameter${task.parameterNames.length === 1 ? '' : 's'}`}>
              <ul className="space-y-1 font-mono text-xs text-muted">
                {task.parameterNames.map((path) => (
                  <li key={path}>{path}</li>
                ))}
              </ul>
            </Block>
          )}

          {/* An empty objectName is not "no object" — it is the root, which is
              what a full refresh queues. Hiding the block there made the
              biggest read the platform makes look like the emptiest task. */}
          {task?.objectName !== null && task?.objectName !== undefined && (
            <Block title="Object">
              {task.objectName === '' ? (
                <span className="text-xs text-muted">
                  The whole data model — every parameter the ONU exposes.
                </span>
              ) : (
                <span className="font-mono text-xs text-muted">{task.objectName}</span>
              )}
            </Block>
          )}

          {task?.fault && (
            <Block title="The CPE refused it" tone="danger">
              <p className="text-sm text-danger">
                {[task.fault.code, task.fault.message].filter(Boolean).join(' · ') ||
                  'No message given.'}
              </p>
              {task.fault.detail && <Pre>{task.fault.detail}</Pre>}
            </Block>
          )}

          {fault?.detail && (
            <Block title="Detail" tone="danger">
              <Pre>{fault.detail}</Pre>
            </Block>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
            <span className="text-xs text-faint">
              {task
                ? 'Queued on the ACS. It is delivered the next time the ONU informs, or immediately on a connection request.'
                : 'Recorded by the ACS when the CPE refused something. Faults clear when the cause is fixed.'}
            </span>
            <div className="flex gap-2">
              {onCancel && (
                <Button size="sm" variant="danger" onClick={onCancel}>
                  Cancel task
                </Button>
              )}
              <Button size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] items-baseline gap-3 px-5 py-2.5">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}

function Block({
  title,
  tone,
  children,
}: {
  title: string;
  tone?: 'danger';
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-line px-5 py-3">
      <p
        className={`mb-2 text-xs font-medium uppercase tracking-wide ${
          tone === 'danger' ? 'text-danger' : 'text-faint'
        }`}
      >
        {title}
      </p>
      {/* Parameter paths are long and must not push the dialog sideways. */}
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/**
 * Parameters whose value is a credential.
 *
 * A queued `setParameterValues` carries the passphrase it is about to write,
 * and this dialog is open to anyone who can read a device — so the value is
 * masked here and read where it is meant to be read, on the WiFi card, which
 * gates it behind an explicit reveal. Which parameter faulted is the useful
 * part anyway, and that is still shown.
 */
function isSecret(path: string): boolean {
  return /passphrase|password|presharedkey|secret|psk/i.test(path);
}

/** The CPE's own words, kept as it sent them. */
function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-lg bg-surface-2 p-3 font-mono text-xs leading-relaxed text-muted">
      {children}
    </pre>
  );
}
